import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Box, FormHelperText, Grid, TextField, Button, Stack, IconButton, Divider, Tooltip } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import api, { resolveAssetUrl } from '../../utils/api';
import {
  EntityFormDialog,
  FormDialogHeader,
  FormDialogContent,
  FormDialogFooter,
  FormSection,
  FormSectionAddButton,
  FormEmptyState,
  FormImageUpload
} from 'components/form/EntityFormDialog';
import { brand, destructive, radius } from 'utils/designTokens';
import useFormValidation from 'hooks/useFormValidation';
import { validators } from 'utils/formValidation';

const removeSlotButtonSx = {
  width: 36,
  height: 36,
  borderRadius: `${radius.sm}px`,
  color: destructive.main,
  bgcolor: destructive.surface,
  border: '1px solid',
  borderColor: destructive.border,
  transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    bgcolor: destructive.surfaceHover,
    borderColor: destructive.borderHover,
    color: destructive.dark,
    boxShadow: destructive.shadow
  }
};

function timeStringToDayjs(str) {
  if (!str || typeof str !== 'string') return null;
  const d = dayjs(`1970-01-01T${str}`);
  return d.isValid() ? d : null;
}

function dayjsToTimeString(v) {
  return v && typeof v.isValid === 'function' && v.isValid() ? v.format('HH:mm') : '';
}

const timeHelperSx = { minHeight: 20, lineHeight: '20px', m: 0, mx: 0 };

function timePickerInputProps(fieldKey, fieldErrorProps) {
  const { error } = fieldErrorProps(fieldKey);
  return {
    fullWidth: true,
    size: 'small',
    error
  };
}

function timePickerHelperText(fieldKey, fieldErrorProps) {
  return fieldErrorProps(fieldKey).helperText || ' ';
}

function collectTimeSlotErrors(times) {
  const errors = {};
  times.forEach((t, index) => {
    if (!t.from_time) {
      errors[`time_${index}_from`] = 'From time is required';
    }
    if (!t.to_time) {
      errors[`time_${index}_to`] = 'To time is required';
    }
  });
  return errors;
}

export default function CategoryFilteredByStoreForm({ open, categoryId, storeId, storeName, onSave, onClose }) {
  const isEdit = Boolean(categoryId);

  const [form, setForm] = useState({
    name_en: '',
    name_ar: '',
    sequence: '',
    image: null
  });

  const [preview, setPreview] = useState(null);
  const [times, setTimes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const { formError, validate, setServerError, setFieldErrors, clearFieldError, fieldErrorProps } = useFormValidation();

  useEffect(() => {
    if (open && isEdit) {
      fetchCategory();
    } else if (open) {
      setForm({ name_en: '', name_ar: '', sequence: '', image: null });
      setTimes([]);
      setPreview(null);
    }
  }, [open, categoryId, isEdit]);

  const fetchCategory = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/product/category/${categoryId}`);
      const data = res.data.data;

      setForm({
        name_en: data.name_en || '',
        name_ar: data.name_ar || '',
        sequence: data.sequence || '',
        image: null
      });

      if (Array.isArray(data.times)) {
        setTimes(
          data.times.map((t) => ({
            from_time: t.from_time || '',
            to_time: t.to_time || ''
          }))
        );
      }

      const imgUrl = data.Doc?.imageUrl || data.Doc?.attachment_url || data.image_url || data.image;
      if (imgUrl) setPreview(resolveAssetUrl(imgUrl));
    } catch (err) {
      console.error('[CategoryForm] fetchCategory error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name } = e.target;
    if (name) clearFieldError(name);
    setForm((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({ ...prev, image: file }));
    setPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const addTime = () => setTimes((prev) => [...prev, { from_time: '', to_time: '' }]);
  const removeTime = (index) => setTimes((prev) => prev.filter((_, i) => i !== index));
  const updateTime = (index, field, value) => {
    clearFieldError(`time_${index}_from`);
    clearFieldError(`time_${index}_to`);
    setTimes((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const ok = validate({ name_en: [validators.required('Name (English)')] }, form);
    if (!ok) return;

    if (!storeId) {
      setServerError('Store selection is required. Choose a store from the list first.');
      return;
    }

    const timeErrors = collectTimeSlotErrors(times);
    if (Object.keys(timeErrors).length) {
      setFieldErrors(timeErrors);
      setServerError('Please complete all time slots or remove empty ones.');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name_en', form.name_en);
      formData.append('name_ar', form.name_ar);
      formData.append('store_id', storeId);
      if (form.sequence !== '') formData.append('sequence', form.sequence);
      if (times.length > 0) formData.append('times', JSON.stringify(times));
      if (form.image instanceof File) {
        formData.append('image', form.image, form.image.name);
      }

      if (isEdit) {
        await api.put(`/product/category/edit/${categoryId}`, formData);
      } else {
        await api.post(`/product/category/add`, formData);
      }
      onSave();
      onClose();
    } catch (err) {
      setServerError(err.response?.data?.message || 'Failed to save category');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <EntityFormDialog open={open} onClose={onClose} maxWidth="sm">
        <FormDialogHeader
          icon={<CategoryRoundedIcon />}
          title={isEdit ? 'Edit Category' : 'New Category'}
          storeName={storeName}
          onClose={onClose}
        />

        <FormDialogContent loading={loading} formError={formError}>
          <Stack spacing={2}>
            <FormSection title="Category details">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Name (English)"
                    name="name_en"
                    value={form.name_en}
                    onChange={handleChange}
                    {...fieldErrorProps('name_en')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Name (Arabic)"
                    name="name_ar"
                    value={form.name_ar}
                    onChange={handleChange}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Sequence"
                    name="sequence"
                    value={form.sequence}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <FormImageUpload
                label="Image"
                preview={preview}
                placeholderIcon={<ImageOutlinedIcon />}
                hasFile={Boolean(form.image)}
                onFileChange={handleFile}
              />
            </FormSection>

            <FormSection
              title="Availability times"
              action={<FormSectionAddButton onClick={addTime}>Add slot</FormSectionAddButton>}
            >
              {times.length === 0 ? (
                <FormEmptyState>Available all day.</FormEmptyState>
              ) : (
                <Stack spacing={1.5} divider={<Divider />}>
                  {times.map((t, index) => (
                    <Box key={index}>
                      <Grid container spacing={1.5} alignItems="center">
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <TimePicker
                            label="From"
                            value={timeStringToDayjs(t.from_time)}
                            onChange={(v) => updateTime(index, 'from_time', dayjsToTimeString(v))}
                            slotProps={{
                              textField: timePickerInputProps(`time_${index}_from`, fieldErrorProps)
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <TimePicker
                            label="To"
                            value={timeStringToDayjs(t.to_time)}
                            onChange={(v) => updateTime(index, 'to_time', dayjsToTimeString(v))}
                            slotProps={{
                              textField: timePickerInputProps(`time_${index}_to`, fieldErrorProps)
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="Remove time slot" arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={() => removeTime(index)}
                              aria-label="Remove time slot"
                              sx={removeSlotButtonSx}
                            >
                              <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>
                      <Grid container spacing={1.5} sx={{ mt: -0.5 }}>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <FormHelperText
                            error={fieldErrorProps(`time_${index}_from`).error}
                            sx={timeHelperSx}
                          >
                            {timePickerHelperText(`time_${index}_from`, fieldErrorProps)}
                          </FormHelperText>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <FormHelperText
                            error={fieldErrorProps(`time_${index}_to`).error}
                            sx={timeHelperSx}
                          >
                            {timePickerHelperText(`time_${index}_to`, fieldErrorProps)}
                          </FormHelperText>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }} />
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              )}
            </FormSection>
          </Stack>
        </FormDialogContent>

        <FormDialogFooter
          onCancel={onClose}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={isEdit ? 'Update category' : 'Create category'}
        />
      </EntityFormDialog>
    </LocalizationProvider>
  );
}
